import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Eye, Pencil, User, ChevronLeft, ChevronRight, X, Download, Upload } from "lucide-react";
import { api, fileUrl, errMsg } from "../lib/api";
import { namaGelar } from "../lib/utils";
import { downloadFile } from "../lib/download";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import SearchableSelect from "../components/SearchableSelect";
import StatusBadge from "../components/StatusBadge";
import { MultiSelect } from "../components/MultiSelect";
import { toast } from "sonner";
import { FotoLightbox } from "../components/FotoLightbox";

export default function JemaatListPage() {
  const navigate = useNavigate();
  const canWrite = useAuth().can("jemaat:write");
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 10, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(null);
  const [search, setSearch] = useState("");
  const [master, setMaster] = useState({});
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    status_jemaat: "", status_keanggotaan: "", komisi: "", jenis_kelamin: "", status_baptisan: "", bidang_pelayanan: [],
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    api.get("/master/options").then((r) => setMaster(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (Array.isArray(v) ? v.length : v) params[k] = Array.isArray(v) ? v.join(",") : v; });
      const r = await api.get("/jemaat", { params });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, filters]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const [exporting, setExporting] = useState(false);
  const doExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (Array.isArray(v) ? v.length : v) params[k] = Array.isArray(v) ? v.join(",") : v; });
      await downloadFile("/jemaat/export/excel", params, "Data_Jemaat.xlsx");
      toast.success("File Excel berhasil diunduh");
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const activeFilterCount = Object.values(filters).filter((v) => (Array.isArray(v) ? v.length : v)).length;
  const clearFilters = () => { setFilters({ status_jemaat: "", status_keanggotaan: "", komisi: "", jenis_kelamin: "", status_baptisan: "", bidang_pelayanan: [] }); setPage(1); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Data Jemaat</h1>
          <p className="text-sm text-slate-500 mt-1">
            Total <span className="font-semibold text-slate-700" data-testid="total-count">{data.total}</span> jemaat terdaftar
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={exporting} onClick={doExport} data-testid="export-jemaat-btn" className="min-h-[44px] rounded-xl">
            <Download className="h-4 w-4 mr-2" /> {exporting ? "Menyiapkan..." : "Export Excel"}
          </Button>
          {canWrite && <Button variant="outline" onClick={() => navigate("/jemaat/import")} data-testid="import-jemaat-btn" className="min-h-[44px] rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import Excel
          </Button>}
          {canWrite && <Button
            onClick={() => navigate("/jemaat/tambah")}
            data-testid="add-jemaat-btn"
            className="min-h-[44px] rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]"
          >
            <Plus className="h-4 w-4 mr-2" /> Tambah Jemaat
          </Button>}
        </div>
      </div>

      <Card className="p-4 rounded-2xl border-slate-200/80 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-jemaat-input"
              placeholder="Cari Nama, NIJ, NIK, No KK, No HP..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 min-h-[44px] rounded-xl"
            />
          </div>
          <Button
            variant="outline"
            data-testid="toggle-filter-btn"
            onClick={() => setShowFilter((s) => !s)}
            className="min-h-[44px] rounded-xl"
          >
            <Filter className="h-4 w-4 mr-2" /> Filter
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-[hsl(24,94%,40%)]">{activeFilterCount}</Badge>
            )}
          </Button>
        </div>

        {showFilter && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="filter-panel">
            <FilterSelect label="Status Jemaat" testId="filter-status-jemaat" options={master.status_jemaat} value={filters.status_jemaat} onChange={(v) => { setFilters((f) => ({ ...f, status_jemaat: v })); setPage(1); }} />
            <FilterSelect label="Status Keanggotaan" testId="filter-status-keanggotaan" options={master.status_keanggotaan} value={filters.status_keanggotaan} onChange={(v) => { setFilters((f) => ({ ...f, status_keanggotaan: v })); setPage(1); }} />
            <FilterSelect label="Komisi" testId="filter-komisi" options={master.komisi} value={filters.komisi} onChange={(v) => { setFilters((f) => ({ ...f, komisi: v })); setPage(1); }} />
            <FilterSelect label="Jenis Kelamin" testId="filter-jenis-kelamin" options={master.jenis_kelamin} value={filters.jenis_kelamin} onChange={(v) => { setFilters((f) => ({ ...f, jenis_kelamin: v })); setPage(1); }} />
            <FilterSelect label="Baptisan" testId="filter-baptisan" options={master.status_baptisan} value={filters.status_baptisan} onChange={(v) => { setFilters((f) => ({ ...f, status_baptisan: v })); setPage(1); }} />
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">Bidang Pelayanan</label>
              <MultiSelect options={master.bidang_pelayanan || []} value={filters.bidang_pelayanan} onChange={(v) => { setFilters((f) => ({ ...f, bidang_pelayanan: v })); setPage(1); }} placeholder="Semua (salah satu dari yang dipilih)" testId="filter-bidang-pelayanan" />
            </div>
            {activeFilterCount > 0 && (
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters} data-testid="clear-filter-btn" className="text-slate-500 min-h-[44px]">
                  <X className="h-4 w-4 mr-1" /> Reset Filter
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {loading ? (
        <div className="text-center py-16 text-slate-400" data-testid="loading">Memuat data...</div>
      ) : data.items.length === 0 ? (
        <Card className="p-12 text-center rounded-2xl" data-testid="empty-state">
          <User className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">Belum ada data jemaat</p>
          <p className="text-sm text-slate-400 mt-1">Tambahkan jemaat baru untuk memulai</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jemaat-grid">
          {data.items.map((j) => (
            <Card
              key={j.id}
              data-testid={`jemaat-card-${j.id}`}
              className="p-4 rounded-2xl border-slate-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              onClick={() => navigate(`/jemaat/${j.id}`)}
            >
              <div className="flex items-start gap-3">
                <Avatar className={`h-14 w-14 rounded-2xl border border-slate-200 ${j.foto_path ? "cursor-zoom-in hover:ring-2 hover:ring-[hsl(224,64%,33%)]/40" : ""}`} role={j.foto_path ? "button" : undefined} title={j.foto_path ? "Klik untuk memperbesar foto" : undefined} onClick={(e) => { if (j.foto_path) { e.stopPropagation(); setZoom({ src: fileUrl(j.foto_path), alt: j.nama_lengkap }); } }} data-testid={`jemaat-foto-${j.id}`}>
                  {j.foto_path ? <AvatarImage src={fileUrl(j.foto_path)} alt={j.nama_lengkap} className="object-cover" /> : null}
                  <AvatarFallback className="rounded-2xl bg-slate-100 text-slate-400">
                    <User className="h-6 w-6" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate group-hover:text-[hsl(224,64%,33%)]">
                    {namaGelar(j)}
                  </div>
                  {j.nama_panggilan && <div className="text-xs text-slate-500">"{j.nama_panggilan}"</div>}
                  <div className="font-mono text-xs text-slate-500 mt-0.5">NIJ: {j.nij}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <StatusBadge value={j.status_jemaat} type="jemaat" />
                <StatusBadge value={j.status_keanggotaan} type="keanggotaan" />
                {j.komisi && <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{j.komisi}</span>}
              </div>
              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                <Button size="sm" variant="ghost" className="flex-1 text-slate-600" onClick={(e) => { e.stopPropagation(); navigate(`/jemaat/${j.id}`); }} data-testid={`detail-btn-${j.id}`}>
                  <Eye className="h-4 w-4 mr-1" /> Detail
                </Button>
                {canWrite && <Button size="sm" variant="ghost" className="flex-1 text-slate-600" onClick={(e) => { e.stopPropagation(); navigate(`/jemaat/${j.id}/edit`); }} data-testid={`edit-btn-${j.id}`}>
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {data.total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>Per halaman:</span>
            <div className="w-24">
              <SearchableSelect
                options={["10", "25", "50"]}
                value={String(pageSize)}
                onChange={(v) => { setPageSize(Number(v || 10)); setPage(1); }}
                allowClear={false}
                testId="page-size-select"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600" data-testid="page-info">
              Halaman {data.page} dari {data.total_pages}
            </span>
            <Button variant="outline" size="icon" className="rounded-xl" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      <FotoLightbox src={zoom?.src} alt={zoom?.alt} onClose={() => setZoom(null)} />
    </div>
  );
}

const FilterSelect = ({ label, options, value, onChange, testId }) => (
  <div>
    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5 block">{label}</label>
    <SearchableSelect options={options || []} value={value} onChange={onChange} placeholder={`Semua`} testId={testId} />
  </div>
);
